// The session balance is either a real number — zero included — or missing.
// Everything that reads the balance must tell those two apart: a missing
// balance switches frontend balance checking off and leaves the check to the
// merchant, while a zero balance is a real value that switches the check on.
//
// The single write into application state used to apply a falsy test, which
// turned a real zero into "missing". These helpers exist so that write, and
// every read below it, share one explicit rule.

type BalanceValue = number | string | null | undefined;

const MISSING_BALANCE_VALUES: BalanceValue[] = [null, undefined, '', 'null'];

/**
 * Normalises a balance as it arrives from the backend. A genuinely absent
 * balance becomes null; every other value, zero included, is kept as-is.
 */
export const sanitizeBalance = (balance: BalanceValue): number | string | null =>
  MISSING_BALANCE_VALUES.indexOf(balance) === -1 ? (balance as number | string) : null;

/**
 * True when the session carries a usable balance. A zero balance is a balance.
 */
export const hasBalance = (balance: BalanceValue): boolean => {
  if (MISSING_BALANCE_VALUES.indexOf(balance) !== -1) return false;

  return !Number.isNaN(Number(balance));
};

/**
 * The balance as a number, or null when no usable balance was supplied.
 */
export const toNumericBalance = (balance: BalanceValue): number | null =>
  hasBalance(balance) ? Number(balance) : null;

// The sample balance preview mode shows when the session supplied none.
export const PREVIEW_MODE_SAMPLE_BALANCE = 1264;

/**
 * The value written into application state for the session balance.
 * A real balance, zero included, survives untouched. Only a genuinely missing
 * balance falls back to the preview-mode sample value.
 */
export const resolveSessionBalance = (
  balance: BalanceValue,
  isPreviewMode?: boolean,
): number | string | null => {
  const sanitized = sanitizeBalance(balance);

  if (sanitized !== null) return sanitized;

  return isPreviewMode ? PREVIEW_MODE_SAMPLE_BALANCE : null;
};

// The two balance messages this story specifies. The wording itself is not
// held in this repository: the translation lookup returns an empty string for
// a key the backend translations payload does not carry, and neither message
// key exists there yet. Until they do, both cases reuse the existing payout
// message key as interim text. See T16.
export const BALANCE_ERROR = {
  INSUFFICIENT: 'insufficient-balance',
  EXCEEDS: 'exceeds-available-balance',
};

export const INTERIM_BALANCE_MESSAGE_KEY = 'pleaseEnterPayout';

interface BalanceRuleInput {
  balance: BalanceValue;
  // The amount the player entered, expressed in the balance's own currency.
  amount?: number | string | null;
  // The minimum transaction limit, expressed in the balance's own currency.
  minimumAmount?: number | string | null;
}

const toComparableNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * The three balance rules, in the order they take precedence:
 *
 * 1. a zero balance with any entered amount gives "Insufficient balance";
 * 2. an entered amount above a positive balance gives "Exceeds available
 *    balance";
 * 3. a minimum transaction limit above a positive balance also gives "Exceeds
 *    available balance".
 *
 * A missing balance switches the frontend check off entirely and returns null,
 * leaving the amount for the merchant to check.
 */
export const resolveBalanceError = ({
  balance,
  amount,
  minimumAmount,
}: BalanceRuleInput): string | null => {
  const numericBalance = toNumericBalance(balance);

  if (numericBalance === null) return null;
  if (numericBalance <= 0) return BALANCE_ERROR.INSUFFICIENT;

  const numericAmount = toComparableNumber(amount);
  if (numericAmount !== null && numericAmount > numericBalance) return BALANCE_ERROR.EXCEEDS;

  const numericMinimum = toComparableNumber(minimumAmount);
  if (numericMinimum !== null && numericMinimum > numericBalance) return BALANCE_ERROR.EXCEEDS;

  return null;
};
